/**
 * Filter numbers a JS number cannot hold. A `bigint` key past 2^53
 * (`9007199254740993`, a 64-bit snowflake id) or a `DECIMAL(38,30)` value
 * rounds when it goes through `Number()` — the filter then selects the
 * neighbouring row. So every place that turns filter text into a number uses
 * `exactNumber`: the number inputs (`FilterValueInput`, the list entry's
 * `toListValue`, both through `numberFieldValue`) and the SQL reader
 * (`parseNumberLiteral`). A value it refuses stays text, in plain decimal
 * (`plainDecimal`: `+9007199254740993` and `1e21` are written
 * `9007199254740993` and `1000000000000000000000`), and the emitters write
 * that text as a bare number on a numeric column (`sqlNumericLiteral`).
 *
 * Why bare and not quoted: PostgreSQL, SQL Server, Oracle and SQLite convert a
 * quoted number to the column's type exactly, but MySQL compares a string with
 * a number as two doubles wherever it does not first convert the constant to
 * the column's integer type — an `IN` list mixing `'9007199254740993'` and
 * `4`, a `BETWEEN` with one quoted bound, any `DECIMAL` column (measured on
 * MySQL 8.0: each matched the neighbouring row too). A bare literal is exact
 * on every engine (MySQL reads it as BIGINT / DECIMAL). Kusto's emitter writes
 * numeric text on a numeric column bare too, and every value on a `decimal`
 * column as a `decimal(...)` literal (`escapeKustoValue`).
 */

import type { FilterCondition } from '../../core/types/workspaceTypes';
import { isNumericColumn } from './columnAlignment';

const NUMBER_TEXT_RE = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/;

/**
 * `text`'s value as sign + significant digits + exponent (`-15e1` for
 * `-1.50`), or `null` when it is not a number. Exported for
 * `filterCompare.ts`'s `numericEquals` — Data Compare's `=`/`IN` need the
 * SAME exact-decimal identity this module uses to write literals, not
 * `Number()` (which rounds a `DECIMAL(38,30)` past double precision).
 */
export function decimalKey(text: string): string | null {
  const m = NUMBER_TEXT_RE.exec(text);
  if (!m || !(m[2] || m[3])) return null;
  const digits = `${m[2]}${m[3] ?? ''}`;
  const lead = digits.search(/[1-9]/);
  if (lead < 0) return '0';
  const exponent = Number(m[4] ?? 0) + m[2]!.length - lead;
  return `${m[1] === '-' ? '-' : ''}${digits.slice(lead).replace(/0+$/, '')}e${exponent}`;
}

/** Plain decimal text (`-12`, `0.5`) — the one spelling every engine reads as the same exact number. */
export const PLAIN_DECIMAL_RE = /^-?\d+(\.\d+)?$/;

/**
 * The JS number `text` spells (`1.50`, `-3`, `.5`, `1e5` …) when the number
 * holds that value exactly and writes it back in plain decimal, else `null` —
 * also for text that is not a number. `1e21` and `1e-7` are held exactly, but
 * `String()` writes them `1e+21` / `1e-7`: an approximate (double) literal on
 * MySQL, where a DECIMAL filter then matches its neighbours too.
 */
export function exactNumber(text: string): number | null {
  const t = text.trim();
  const key = decimalKey(t);
  const n = Number(t);
  return key !== null && PLAIN_DECIMAL_RE.test(String(n)) && decimalKey(String(n)) === key ? n : null;
}

/**
 * The most digits an exponent spelling is written out to (`plainDecimal`) —
 * MySQL's widest DECIMAL. Past it the value is refused (the number inputs
 * show it invalid) rather than written as a huge literal.
 */
export const MAX_EXPANDED_DIGITS = 65;

/**
 * `text` in plain decimal: plain text as it is, any other number spelling
 * written out (`+5` → `5`, `.5` → `0.5`, `5.` → `5`, `1.5e3` → `1500`,
 * `2E-3` → `0.002`). `null` when `text` is not a number, or its exponent
 * writes out to more than `MAX_EXPANDED_DIGITS` digits.
 */
export function plainDecimal(text: string): string | null {
  const t = text.trim();
  if (PLAIN_DECIMAL_RE.test(t)) return t;
  const m = NUMBER_TEXT_RE.exec(t);
  if (!m || !(m[2] || m[3])) return null;
  const all = `${m[2]}${m[3] ?? ''}`;
  const significant = all.replace(/^0+/, '');
  const digits = significant.replace(/0+$/, '');
  if (!digits) return '0';
  // The decimal point sits `point` digits into `digits` (at or below 0: that many zeros before them).
  const point = m[2]!.length - (all.length - significant.length) + Number(m[4] ?? 0);
  if ((point > 0 ? Math.max(point, digits.length) : 1 - point + digits.length) > MAX_EXPANDED_DIGITS) return null;
  const whole = point > 0 ? digits.slice(0, point).padEnd(point, '0') : '0';
  const fraction = point > 0 ? digits.slice(point) : `${'0'.repeat(-point)}${digits}`;
  return `${m[1] === '-' ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
}

/**
 * What a number field holds for the text typed or pasted into it (the number
 * inputs, the list entry): the JS number when it holds the value exactly
 * (`exactNumber`), else the number's plain decimal text (`plainDecimal`),
 * which the emitters write bare. `null` for a number whose exponent writes
 * out past `MAX_EXPANDED_DIGITS` (refused); text that is not a number stays
 * as it is.
 */
export function numberFieldValue(text: string): number | string | null {
  return exactNumber(text) ?? plainDecimal(text) ?? (decimalKey(text.trim()) === null ? text : null);
}

/**
 * `value`'s plain decimal text when it is number text no JS number holds
 * exactly (`'9007199254740993'`, `'+9007199254740993'`, `'1e21'`), else
 * `undefined`. Only digits, a sign and a point: never text to write bare.
 */
export function inexactNumberText(value: unknown): string | undefined {
  if (typeof value !== 'string' || exactNumber(value) !== null) return undefined;
  return plainDecimal(value) ?? undefined;
}

/**
 * The bare numeric literal for `value` compared with a column of `dataType`,
 * or `undefined` when the policy does not apply (not a numeric column, or not
 * a number too precise for a JS number) — the caller then uses its ordinary
 * escaper. Shared, like `sqlTemporalLiteral`, by the renderer's
 * `filterWhereBuilder.ts` and the Table Explorer generators' `queryUtils.ts`.
 */
export function sqlNumericLiteral(value: unknown, dataType: string | undefined): string | undefined {
  return dataType && isNumericColumn({ dataType }) ? inexactNumberText(value) : undefined;
}

/**
 * A filter set holding such a number — the Table Explorer providers read the
 * column types for it (`columnTypesNeededFor`), so the value is written bare.
 */
export function hasInexactNumber(filters: readonly { readonly conditions: readonly FilterCondition[] }[] | undefined): boolean {
  return (filters ?? []).some((g) =>
    g.conditions.some((c) => [c.value, c.value2].flat().some((v) => inexactNumberText(v) !== undefined)),
  );
}
